// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Durable outbound buffer for sealed battery segments, plus the open segment being filled.
//
// Modelled on services/courseTraceQueue.ts, and deliberately a separate copy rather than a
// shared abstraction: the caps, the outcome semantics (a WebSocket ack rather than an HTTP
// status) and the "open segment" slot are all different, and one generic queue serving both
// would be harder to read than two small ones.
//
// The open segment is written through on every sample. That is the whole point: a segment
// that only exists in memory is lost to the process death that ends most rounds.

import type { BatterySegment } from '../features/battery-telemetry/types';

type AsyncStorageLike = {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
};

/**
 * The segment currently being filled. Not yet sealed, so it has no end timestamps.
 *
 * segmentId is minted when the segment OPENS, not when it seals, and rides along in storage —
 * that is what lets a segment rebuilt after a process death keep its identity, so the server
 * recognises the retry instead of storing a second copy of the same hour.
 */
export type OpenBatterySegment = Omit<
  BatterySegment,
  'endedAtUtc' | 'sealedAtUtc' | 'sealReason' | 'sampleCount'
> & {
  /** Monotonic clock reading when the segment opened; `t` is measured from it. */
  startedAtMonotonicMs: number;
};

type PendingSegment = {
  segment: BatterySegment;
  enqueuedAt: string;
  attempts: number;
};

type Envelope = {
  version: 1;
  installId: string | null;
  /** Wall-clock ms until which sampling is suppressed by a server "disabled" ack. */
  pausedUntilMs: number;
  open: OpenBatterySegment | null;
  segments: PendingSegment[];
};

const STORAGE_VERSION = 1;

/** Twelve hours of unsent telemetry. Beyond that the device is simply never getting online. */
const MAX_PENDING_SEGMENTS = 12;
const MAX_ATTEMPTS = 20;
const MAX_SEGMENT_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const STORAGE_KEY = `goffy:battery-telemetry:v${STORAGE_VERSION}`;

let asyncStorage: AsyncStorageLike | null = null;
try {
  asyncStorage = require('@react-native-async-storage/async-storage').default as AsyncStorageLike;
} catch {
  asyncStorage = null;
}

let storageOverride: AsyncStorageLike | null = null;

const storage = (): AsyncStorageLike | null => storageOverride ?? asyncStorage;

const emptyEnvelope = (): Envelope => ({
  version: STORAGE_VERSION,
  installId: null,
  pausedUntilMs: 0,
  open: null,
  segments: [],
});

let envelope: Envelope = emptyEnvelope();
let loaded = false;
let loadPromise: Promise<void> | null = null;

const normalize = (value: unknown): Envelope => {
  if (!value || typeof value !== 'object') return emptyEnvelope();
  const input = value as Record<string, unknown>;
  if (input.version !== STORAGE_VERSION || !Array.isArray(input.segments)) return emptyEnvelope();

  const segments = input.segments.filter((entry): entry is PendingSegment => {
    if (!entry || typeof entry !== 'object') return false;
    const segment = (entry as Record<string, unknown>).segment as BatterySegment | undefined;
    return Boolean(segment && typeof segment.segmentId === 'string' && Array.isArray(segment.samples));
  });

  const open = input.open as OpenBatterySegment | null | undefined;
  return {
    version: STORAGE_VERSION,
    installId: typeof input.installId === 'string' ? input.installId : null,
    pausedUntilMs: typeof input.pausedUntilMs === 'number' ? input.pausedUntilMs : 0,
    open: open && Array.isArray(open.samples) ? open : null,
    segments,
  };
};

const persist = async (): Promise<void> => {
  const store = storage();
  if (!store) return;
  await store.setItem(STORAGE_KEY, JSON.stringify(envelope));
};

const ensureLoaded = async (): Promise<void> => {
  if (loaded) return;
  if (!loadPromise) {
    loadPromise = (async () => {
      const store = storage();
      if (store) {
        try {
          const raw = await store.getItem(STORAGE_KEY);
          envelope = raw ? normalize(JSON.parse(raw)) : emptyEnvelope();
        } catch {
          // A corrupt envelope is not worth failing a round over; start clean.
          envelope = emptyEnvelope();
        }
      }
      loaded = true;
    })();
  }
  await loadPromise;
};

const applyGiveUpPolicy = (): void => {
  const cutoff = Date.now() - MAX_SEGMENT_AGE_MS;
  envelope.segments = envelope.segments.filter((entry) => {
    const enqueuedAtMs = Date.parse(entry.enqueuedAt);
    const tooOld = Number.isFinite(enqueuedAtMs) && enqueuedAtMs < cutoff;
    return !tooOld && entry.attempts < MAX_ATTEMPTS;
  });
  if (envelope.segments.length > MAX_PENDING_SEGMENTS) {
    envelope.segments = envelope.segments.slice(-MAX_PENDING_SEGMENTS);
  }
};

export const loadBatteryEnvelope = async (): Promise<{
  installId: string | null;
  pausedUntilMs: number;
  open: OpenBatterySegment | null;
}> => {
  await ensureLoaded();
  return { installId: envelope.installId, pausedUntilMs: envelope.pausedUntilMs, open: envelope.open };
};

export const setBatteryInstallId = async (installId: string): Promise<void> => {
  await ensureLoaded();
  if (envelope.installId === installId) return;
  envelope.installId = installId;
  await persist();
};

/** Persists the in-progress segment. Called after every sample — see the file header. */
export const saveOpenBatterySegment = async (open: OpenBatterySegment | null): Promise<void> => {
  await ensureLoaded();
  envelope.open = open;
  await persist();
};

export const enqueueBatterySegment = async (segment: BatterySegment): Promise<void> => {
  await ensureLoaded();
  if (envelope.segments.some((entry) => entry.segment.segmentId === segment.segmentId)) return;
  envelope.segments.push({ segment, enqueuedAt: new Date().toISOString(), attempts: 0 });
  applyGiveUpPolicy();
  await persist();
};

export const getPendingBatterySegments = async (): Promise<BatterySegment[]> => {
  await ensureLoaded();
  return envelope.segments.map((entry) => entry.segment);
};

/** Drops an acked (or permanently rejected) segment. */
export const removeBatterySegment = async (segmentId: string): Promise<void> => {
  await ensureLoaded();
  const before = envelope.segments.length;
  envelope.segments = envelope.segments.filter((entry) => entry.segment.segmentId !== segmentId);
  if (envelope.segments.length !== before) {
    await persist();
  }
};

export const recordBatterySendAttempt = async (segmentId: string): Promise<void> => {
  await ensureLoaded();
  const entry = envelope.segments.find((candidate) => candidate.segment.segmentId === segmentId);
  if (!entry) return;
  entry.attempts += 1;
  applyGiveUpPolicy();
  await persist();
};

/**
 * The server kill switch. Everything queued is dropped rather than held: the server has said it
 * does not want this data, and keeping it would only make the device retry for days.
 */
export const pauseBatteryTelemetry = async (durationMs: number): Promise<void> => {
  await ensureLoaded();
  envelope.segments = [];
  envelope.open = null;
  envelope.pausedUntilMs = Date.now() + durationMs;
  await persist();
};

export const __setBatteryTelemetryStorageForTests = (store: AsyncStorageLike | null) => {
  storageOverride = store;
};

export const __resetBatteryTelemetryQueueForTests = () => {
  envelope = emptyEnvelope();
  loaded = false;
  loadPromise = null;
};
